import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DashboardSummary, LeaveStatus } from "@/types";
import { formatLeaveType } from "@/utils/leaveType";
import {
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  UserMinus,
  XCircle,
} from "lucide-react";
import { LeaveBalanceCard } from "./LeaveBalanceCard";

interface EmployeeSummaryProps {
  data?: DashboardSummary;
  isLoading: boolean;
}

export function EmployeeSummary({ data, isLoading }: EmployeeSummaryProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-muted rounded" />
              <div className="h-4 w-4 bg-muted rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-12 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!data || !data.myLeaves) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center p-6 text-muted-foreground">
          <FileText className="h-8 w-8 mb-2 opacity-50" />
          <p>No personal dashboard data available</p>
        </CardContent>
      </Card>
    );
  }

  const { myLeaves } = data;

  const stats = [
    {
      label: "Total Leaves",
      value: myLeaves.total,
      icon: FileText,
      color: "text-blue-500",
    },
    {
      label: "Pending Requests",
      value: myLeaves.pending,
      icon: Clock,
      color: "text-yellow-500",
    },
    {
      label: "Approved",
      value: myLeaves.approved,
      icon: CheckCircle2,
      color: "text-green-500",
    },
    {
      label: "Rejected",
      value: myLeaves.rejected,
      icon: XCircle,
      color: "text-red-500",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Personal Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.label}
                </CardTitle>
                <Icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* My Upcoming Leaves */}
        <Card className="col-span-1 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Upcoming Leaves
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.myUpcomingLeaves && data.myUpcomingLeaves.length > 0 ? (
              <div className="space-y-4">
                {data.myUpcomingLeaves.map((leave) => (
                  <div
                    key={leave.id}
                    className="flex items-center justify-between border-b last:border-0 pb-2 last:pb-0"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">
                        {formatLeaveType(leave.type)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {(() => {
                          const startDate = new Date(leave.startDate);
                          const endDate = new Date(leave.endDate);
                          return `${startDate.toLocaleDateString()} (${startDate.toLocaleDateString('en-US', { weekday: 'long' })}) - ${endDate.toLocaleDateString()} (${endDate.toLocaleDateString('en-US', { weekday: 'long' })})`;
                        })()}
                      </span>
                    </div>
                    <Badge
                      variant={
                        leave.status === LeaveStatus.APPROVED
                          ? "default"
                          : "outline"
                      }
                      className="text-xs"
                    >
                      {leave.status === "PENDING" ? "PENDING" : leave.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No upcoming leaves
              </p>
            )}
          </CardContent>
        </Card>

        {/* Team On Leave Today */}
        <Card className="col-span-1 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserMinus className="h-5 w-5 text-warning" />
              On Leave Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.teamOnLeaveToday && data.teamOnLeaveToday.length > 0 ? (
              <div className="space-y-4">
                {data.teamOnLeaveToday.map((leave, i) => (
                  <div
                    key={i}
                    className="flex flex-col space-y-1 border-b last:border-0 pb-2 last:pb-0"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">{leave.name}</span>
                      <div className="flex gap-2">
                        <Badge variant="outline" className="text-xs">
                          {leave.teamName}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {formatLeaveType(leave.leaveType)}
                        </Badge>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(leave.startDate).toLocaleDateString()} -{" "}
                      {new Date(leave.endDate).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No team members on leave
              </p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Holidays */}
        <Card className="col-span-1 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-500" />
              Upcoming Holidays
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.upcomingHolidays && data.upcomingHolidays.length > 0 ? (
              <div className="space-y-4">
                {data.upcomingHolidays.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="flex items-center justify-between border-b last:border-0 pb-2 last:pb-0"
                  >
                    <span className="font-medium text-sm">{holiday.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {new Date(holiday.date).toLocaleDateString()}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No upcoming holidays
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Leave Balances Section */}
      <LeaveBalanceCard balances={data.leaveBalances} />
    </div>
  );
}
